import { useState } from 'react';

import type { BiomeKind } from '../generated/BiomeKind';
import type { ServerVisibility } from '../generated/ServerVisibility';
import type { ChunkCoordJson } from '../platform/api';
import { useEmbark } from '../platform/store';
import styles from './embark.module.css';

export function FoundWorldPanel({
  chunk,
  biome,
  onCancel,
  onFounded,
}: {
  chunk: ChunkCoordJson;
  biome: BiomeKind | null;
  onCancel: () => void;
  onFounded: (claimId: number) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<ServerVisibility>('Public');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const doFoundWorld = useEmbark((s) => s.doFoundWorld);

  const validate = (v: string): string => {
    if (v.length === 0) return 'Name is required';
    if (v.length > 64) return 'Name must be 64 characters or fewer';
    if (!/^[a-zA-Z0-9 -]+$/.test(v))
      return 'Only letters, numbers, spaces, and hyphens allowed';
    return '';
  };

  const handleNameChange = (v: string) => {
    setName(v);
    setError(validate(v));
  };

  const handleSubmit = async () => {
    const err = validate(name);
    if (err) {
      setError(err);
      return;
    }
    setSubmitting(true);
    const result = await doFoundWorld(name, visibility);
    if (result) {
      onFounded(result.claim_id);
    } else {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.panelOverlay} onClick={onCancel}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.panelTitle}>Found a Settlement</div>

        <div className={styles.panelField}>
          <span className={styles.panelLabel}>Chunk</span>
          <span className={styles.panelValue}>
            ({chunk.x}, {chunk.y})
          </span>
        </div>

        {biome && (
          <div className={styles.panelField}>
            <span className={styles.panelLabel}>Biome</span>
            <span className={styles.panelValue}>{biome}</span>
          </div>
        )}

        <div className={styles.panelField}>
          <label className={styles.panelLabel} htmlFor="claim-name">
            Name your world
          </label>
          <input
            id="claim-name"
            className={styles.panelInput}
            type="text"
            maxLength={64}
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="My World"
            autoFocus
          />
          {error && <div className={styles.panelError}>{error}</div>}
        </div>

        <div className={styles.panelField}>
          <span className={styles.panelLabel}>Visibility</span>
          <div className={styles.radioGroup}>
            {(['Public', 'FriendsOnly', 'Private'] as ServerVisibility[]).map(
              (v) => (
                <label key={v} className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="visibility"
                    value={v}
                    checked={visibility === v}
                    onChange={() => setVisibility(v)}
                  />
                  {v === 'FriendsOnly' ? 'Friends Only' : v}
                </label>
              ),
            )}
          </div>
          <div style={{ fontSize: 11, color: '#949ba4', marginTop: 4 }}>
            Controls who can join as a player. SOMAs always travel freely.
          </div>
        </div>

        <div className={styles.panelActions}>
          <button
            className={styles.primaryBtn}
            disabled={submitting || !!error || name.length === 0}
            onClick={() => void handleSubmit()}
          >
            {submitting ? 'Founding...' : 'Found Settlement'}
          </button>
          <button className={styles.secondaryBtn} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
